/**
 * Central error translator. EVERY component that catches an HTTP error
 * should route through this instead of reading `err.response.data.message`
 * directly — that direct path is how raw SQL / schema details leaked into
 * user-facing <Alert>s. See docs/infrastructure/error-handling.md.
 *
 * Return shape: `{ code, status, traceId, fieldErrors, context, message, raw }`. Only
 * `message` should be shown to users; `raw` is for debugging in DevTools.
 *
 * `context` is the structured detail an authored handler attaches
 * (BookingException's `context`, client-visible by contract). It is read only
 * from a response that carries a code — the same trust gate as the message —
 * and is an empty object otherwise. Branch on it for a recovery action, such
 * as which confirmation to open for CART_ALREADY_BOOKED; never for copy.
 *
 * `fieldErrors` is Laravel's validation map flattened to one message per
 * field — `{ email: 'The email has already been taken.' }`. Present on every
 * return; an empty object when the failure was not a validation error, so a
 * caller can spread it into form state without a null check.
 *
 * It exists because 22 call sites across 17 files were each reaching into
 * `err.response.data.errors` and flattening it by hand, four different ways.
 * That is the same raw-body read the CI guard forbids elsewhere — it slipped
 * through only because the guard's pattern matches `.message`, not `.errors`.
 *
 * `traceId` is the backend's support lookup key, present on every return so
 * any surface can show it. It is folded into `message` for 5xx only — a 5xx
 * means "contact support", where the reference is the whole point, while a
 * 4xx means "fix your input", where it is noise.
 *
 * OWNERSHIP: the BACKEND owns error copy; this module does not map codes
 * to text. Every authored backend handler emits user-facing copy by
 * contract, so duplicating it here would just create drift — and did:
 * the old CODE_MESSAGE map restated six backend strings that had since
 * diverged. If you need per-code behaviour on the frontend, branch on
 * `code` to pick a RECOVERY ACTION (which button to show, which query to
 * invalidate) — never to rewrite the message.
 *
 * The one thing to revisit: if i18n is ever introduced, copy has to move
 * here, keyed by `code`. Keeping `code` stable is what makes that cheap.
 */

// Status-based fallbacks. 5xx MUST always resolve here — never to a
// backend-supplied message — to keep any accidental raw text from
// escaping the server. 4xx allows the backend message ONLY when the
// response includes a trusted `code`, meaning we authored it.
const STATUS_MESSAGE = {
  400: 'That request looks off. Please review and try again.',
  401: 'Session expired. Please sign in again.',
  403: "You don't have permission to do that.",
  404: 'Not found.',
  409: 'This conflicts with existing data.',
  422: 'Please fix the highlighted fields.',
  429: 'Too many requests. Please slow down and try again.',
  500: 'Something went wrong on our end. Please try again or contact support.',
  502: 'Upstream service didn\'t respond in time. Please try again.',
  503: 'The service is temporarily unavailable. Please try again in a moment.',
  504: 'The request timed out. Please try again.',
};

export function parseApiError(err) {
  const status = err?.response?.status ?? null;
  const data   = err?.response?.data ?? {};

  // Resolve the code from every position our authored handlers use:
  //   error_code  — BookingException / PolicyException
  //   code        — catch-all + Encompass handlers in bootstrap/app.php
  //   errorCode   — DerbyErrorMapper::formatForResponse
  //   error.code  — BillingException::toArray (nested)
  const code =
    data.error_code ??
    data.code ??
    data.errorCode ??
    data.error?.code ??
    null;

  const beMsg = data.message ?? data.error?.message ?? data.errorMessage ?? null;

  // The support lookup key. bootstrap/app.php mints a UUID per failure, logs
  // the scrubbed detail against it, writes an msi_error_logs row, and returns
  // it top-level. Everything the user is shown should carry it — it is the
  // only way a screenshot becomes a row someone can open.
  //
  // Read here rather than at each display site: http.js used to be the only
  // reader, so its toast carried the reference while the inline alert on the
  // same failure did not — and the inline one is what people screenshot.
  const traceId = data.trace_id ?? data.traceId ?? data.error?.trace_id ?? null;

  // Laravel sends { errors: { field: ["first", "second"] } }. Callers almost
  // always want one message per field, so flatten here rather than in every
  // form. The rare caller that needs every message still has `raw`.
  //
  // Not gated on status 422: an authored handler can attach field errors to a
  // 400 or 409, and a caller checking `Object.keys(fieldErrors).length` should
  // not have to know which status the backend chose.
  const rawFieldErrors = data.errors ?? data.error?.errors ?? null;
  const fieldErrors = {};
  if (rawFieldErrors && typeof rawFieldErrors === 'object' && !Array.isArray(rawFieldErrors)) {
    for (const [field, messages] of Object.entries(rawFieldErrors)) {
      const first = Array.isArray(messages) ? messages[0] : messages;
      if (first != null && first !== '') fieldErrors[field] = String(first);
    }
  }

  const authoredContext = code ? data.context : null;
  const context =
    authoredContext && typeof authoredContext === 'object' && !Array.isArray(authoredContext)
      ? authoredContext
      : {};

  // 5xx: NEVER surface a backend message — even if the response has one.
  // The handlers in bootstrap/app.php all produce safe copy, but if some
  // path ever slips a raw exception message into a 500, this is the last
  // line of defense.
  //
  // Appending the reference rather than trusting the backend's copy to carry
  // it: the database_error handler already writes "Reference: {id}" into its
  // message, and discarding that message here discarded the reference with
  // it. Appending means every 5xx gets one, not just the handlers that
  // remembered.
  if (status && status >= 500) {
    const base = STATUS_MESSAGE[status] ?? STATUS_MESSAGE[500];
    return {
      code,
      status,
      traceId,
      fieldErrors,
      context,
      message: traceId ? `${base} Reference: ${traceId}` : base,
      raw: err,
    };
  }

  // 4xx: the presence of a CODE is the signal that WE authored this
  // response, and every authored handler emits user-facing copy by
  // contract (see App\Exceptions\Booking\BookingException). So the
  // message is safe to render as-is.
  //
  // This replaced a hand-maintained TRUSTED_BE_CODES allowlist. The
  // allowlist had to be updated for every new backend error code, and
  // forgetting silently degraded the message to a generic — a failure
  // mode with no signal. Keying on "did we author this?" instead is
  // zero-maintenance and preserves the same guarantee, because Laravel's
  // own 4xx exceptions carry no code:
  //
  //   ValidationException      -> { message, errors }        (no code)
  //   ModelNotFoundException   -> { message: "No query
  //                                 results for model [...]" } (no code)
  //   Spatie unauthorized      -> { message: "User does not
  //                                 have the right permissions." } (no code)
  //
  // All three therefore fall through to the status generic below rather
  // than leaking a model class name or framework-internal phrasing.
  if (status && status < 500 && code && beMsg) {
    return { code, status, traceId, fieldErrors, context, message: beMsg, raw: err };
  }

  // Status-based fallback.
  if (status && STATUS_MESSAGE[status]) {
    return { code, status, traceId, fieldErrors, context, message: STATUS_MESSAGE[status], raw: err };
  }

  // Network error / no response.
  return {
    code,
    status,
    traceId,
    fieldErrors,
    context,
    message: 'Something went wrong. Please try again.',
    raw: err,
  };
}
